import { useMemo } from 'react';
import type { RoleScope } from '../services/auth/roleResolver';
import type { RoleContext } from '../lib/roles';
import { sortDepartmentsByLofThenName } from '../lib/departmentUi';
import { normalizeLofId } from '../lib/lofs';
import { sanitizeDepartmentIds } from '../services/medErrorIncidentData';
import { normalizeDepartmentId } from '../utils/normalizeIds';
import type { TeamDepartmentFilterOption } from '../components/TeamDepartmentFilterBar';

type DeptRow = { id: string; name?: string | null; lofId?: string | null };

function deptToFilterOption(d: DeptRow): TeamDepartmentFilterOption {
  const lofRaw = typeof d.lofId === 'string' && d.lofId.trim() ? d.lofId.trim() : '';
  return {
    id: normalizeDepartmentId(d.id),
    name: (typeof d.name === 'string' ? d.name : d.id) as string,
    ...(lofRaw ? { lofId: normalizeLofId(lofRaw) } : {}),
  };
}

/**
 * Privileged incident nav: universal list — all known departments (for client-side narrowing).
 */
export function useIncidentNavDepartmentFilterOptions(departments: DeptRow[]): TeamDepartmentFilterOption[] {
  return useMemo(() => {
    if (!departments.length) return [];
    return sortDepartmentsByLofThenName(
      departments.map(deptToFilterOption).filter((d) => d.id),
    );
  }, [departments]);
}

export type TeamNavDepartmentFilterOptionsParams = {
  /**
   * When true, list every known department (same as wildcard scope), e.g. NN on nurse_notes or
   * ethic team on /team/ethic — while base role scope stays LOF-restricted for other categories.
   */
  expandToAllDepartments?: boolean;
};

/**
 * Privileged team nav: scoped list — wildcard roles see all; SN sees LOF; others use role scope (no '*').
 * Optional expandToAllDepartments overrides scope to full hospital list (team-role + category gated in layout).
 */
export function useTeamNavDepartmentFilterOptions(
  departments: DeptRow[],
  roleScope: RoleScope | null | undefined,
  roleContext: RoleContext | null | undefined,
  params?: TeamNavDepartmentFilterOptionsParams,
): TeamDepartmentFilterOption[] {
  const expandToAllDepartments = params?.expandToAllDepartments === true;
  return useMemo(() => {
    if (!departments.length) return [];
    const hasWildcardScope = (roleScope?.view?.departmentIds ?? roleScope?.departmentIds ?? []).includes('*');
    if (hasWildcardScope || expandToAllDepartments) {
      return sortDepartmentsByLofThenName(
        departments.map(deptToFilterOption).filter((d) => d.id),
      );
    }
    const raw = sanitizeDepartmentIds(roleContext?.lineOfWorkDepartmentIds ?? []);
    if (raw.length) {
      const idSet = new Set(raw.map((id) => normalizeDepartmentId(id)));
      return sortDepartmentsByLofThenName(
        departments.filter((d) => idSet.has(normalizeDepartmentId(d.id))).map(deptToFilterOption),
      );
    }
    const scopeIds = sanitizeDepartmentIds([
      ...(roleScope?.view?.departmentIds ?? []),
      ...(roleScope?.departmentIds ?? []),
    ]).filter((id) => id !== '*');
    if (scopeIds.length) {
      const idSet = new Set(scopeIds.map((id) => normalizeDepartmentId(id)));
      return sortDepartmentsByLofThenName(
        departments.filter((d) => idSet.has(normalizeDepartmentId(d.id))).map(deptToFilterOption),
      );
    }
    return [];
  }, [
    departments,
    expandToAllDepartments,
    roleScope?.departmentIds,
    roleScope?.view?.departmentIds,
    roleContext?.lineOfWorkDepartmentIds,
  ]);
}
